import React, { createRef, useCallback, useMemo } from 'react'
import { View, useWindowDimensions } from 'react-native'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { createComponent, ColorUtils } from 'react-native-small-ui'
import { isNumber } from 'lodash'
import { ReduceMotion } from 'react-native-reanimated'
import { create } from 'zustand'

import { isWeb, useMaxWidth } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { Chips } from '../../components/GameAssets/Chips'
import { Avatar } from '../../components/ANewDesign/PlayerCell/AvatarTimer/Avatar'
import { useHandHistoryQuery } from '../../hooks/convex/useGameState'
import { useGameContext } from './GameContext'
import { translate } from '../../i18n'
import { buildPlayerColorMap } from '../../utils/playerColors'

export const sidePotWinnersSheetRef = createRef<BottomSheetModal>()

export const useSidePotWinnersSheetStore = create<{ isOpen: boolean }>(() => ({ isOpen: false }))

export const useSidePotWinnersSheet = () => {
  const open = useCallback(() => {
    useSidePotWinnersSheetStore.setState({ isOpen: true })
    sidePotWinnersSheetRef.current?.present()
  }, [])

  const close = useCallback(() => {
    useSidePotWinnersSheetStore.setState({ isOpen: false })
    sidePotWinnersSheetRef.current?.close()
  }, [])

  return {
    ref: sidePotWinnersSheetRef,
    open,
    close,
    isOpen: useSidePotWinnersSheetStore((s) => s.isOpen)
  }
}

export const SidePotWinnersSheet = () => {
  const insets = useSafeAreaInsets()
  const { width: screenWidth } = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const _maxWidth = isNumber(maxWidth) ? maxWidth : screenWidth
  const marginHorizontal = screenWidth > _maxWidth ? (screenWidth - _maxWidth) / 2 : 0

  const { gameId } = useGameContext()
  const { handHistory } = useHandHistoryQuery(gameId)

  const mostRecentHand = handHistory?.[0]
  const potWinners = mostRecentHand?.potDistribution?.potWinners ?? []

  const playerColorMap = useMemo(() => {
    const allPlayerIds = new Set<string>()
    potWinners.forEach((pot) => pot.winners.forEach((w) => allPlayerIds.add(w.playerId)))
    return buildPlayerColorMap([...allPlayerIds])
  }, [potWinners])

  const renderBackdrop = useCallback((props: React.ComponentProps<typeof BottomSheetBackdrop>) => {
    return <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.3} />
  }, [])

  return (
    <BottomSheetModal
      ref={sidePotWinnersSheetRef}
      overrideReduceMotion={ReduceMotion.System}
      enableDynamicSizing={true}
      enableContentPanningGesture={false}
      enablePanDownToClose={true}
      enableHandlePanningGesture={true}
      enableOverDrag={false}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={{ backgroundColor: '#0003' }}
      backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
      containerStyle={{
        marginLeft: !isWeb && marginHorizontal > 0 ? marginHorizontal : undefined,
        shadowColor: DS.palette['Shades/Maroon 2'],
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,
        elevation: 6
      }}
      onDismiss={() => {
        useSidePotWinnersSheetStore.setState({ isOpen: false })
      }}
      style={{
        zIndex: 9999,
        marginHorizontal: 'auto',
        maxWidth: maxWidth
      }}
    >
      <BottomSheetView
        style={{
          flexGrow: 1,
          paddingHorizontal: 16,
          paddingVertical: 12,
          paddingBottom: insets.bottom || 16,
          gap: 16
        }}
      >
        <DS.Text variant="Heading 3/Medium" uppercase>
          {translate('newGameScreen.common.winners')}
        </DS.Text>

        {potWinners.map((pot, index) => {
          const isMainPot = pot.potIndex === 0
          const label = isMainPot
            ? translate('newGameScreen.common.mainPot')
            : `${translate('newGameScreen.common.sidePot')} ${pot.potIndex}`

          return (
            <PotWinnersSection key={`pot-${pot.potIndex}`}>
              <DS.Text variant="Caption/Bold" uppercase style={{ marginBottom: 8 }}>
                {label}
              </DS.Text>
              <View style={{ gap: 8 }}>
                {pot.winners.map((winner) => {
                  const color = DS.palette[playerColorMap.get(winner.playerId) || 'Brand/Maroon']
                  return (
                    <WinnerRow key={winner.playerId}>
                      <Avatar
                        name={winner.playerName}
                        containerStyle={{ backgroundColor: color }}
                        textStyle={{ color: ColorUtils.getContrastColor(color) }}
                      />
                      <View style={{ flex: 1 }}>
                        <DS.Text variant="Body Large/Medium">{winner.playerName}</DS.Text>
                        {winner.hand?.description ? (
                          <DS.Text variant="Body Small/Regular" style={{ opacity: 0.6 }}>
                            {winner.hand.description}
                          </DS.Text>
                        ) : null}
                      </View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Chips.Icon width={18} height={18} color={DS.palette['Brand/Maroon']} />
                        <DS.Text variant="Body Large/Medium" uppercase>
                          {winner.amount}
                        </DS.Text>
                      </View>
                    </WinnerRow>
                  )
                })}
              </View>
            </PotWinnersSection>
          )
        })}
      </BottomSheetView>
    </BottomSheetModal>
  )
}

const PotWinnersSection = createComponent(View, {
  width: '100%'
})

const WinnerRow = createComponent(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  padding: 12,
  paddingHorizontal: 16,
  borderRadius: 4,
  borderWidth: 0.5,
  borderColor: '#ccc'
})
